import {
	BottomSheetBackdrop,
	type BottomSheetBackdropProps,
	BottomSheetFlatList,
	BottomSheetModal,
} from "@gorhom/bottom-sheet";
import { isWeb } from "@memorang/helpers/platform";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, FlatList, Pressable, StyleSheet } from "react-native";
import { themeRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import EmptyState from "../EmptyView";
import SideSheet from "../SideSheet";
import { SessionItemsNavEmptyIllustration } from "./SessionItemsNavEmptyIllustration";
import {
	SessionItemsNavFiltersSection,
	SessionItemsNavHeader,
	SessionItemsNavItemsTitle,
	SessionItemsNavRow,
} from "./SessionItemsNavPaneViews";
import {
	SESSION_ITEMS_NAV_EMPTY_CTA,
	SESSION_ITEMS_NAV_EMPTY_FILTERED_SUBTITLE,
	SESSION_ITEMS_NAV_EMPTY_FILTERED_TITLE,
	SESSION_ITEMS_NAV_EMPTY_NO_ITEMS_SUBTITLE,
	SESSION_ITEMS_NAV_EMPTY_NO_ITEMS_TITLE,
} from "./constants";
import { matchesSessionItemsNavFilter } from "./helpers";
import type {
	SessionItemsNavFilterValue,
	SessionItemsNavPaneProps,
} from "./types";

export type {
	SessionItemsNavFilterValue,
	SessionItemsNavItem,
	SessionItemsNavItemStatus,
	SessionItemsNavPresentation,
	SessionItemsNavPaneProps,
} from "./types";

const { width: screenWidth } = Dimensions.get("window");

type SessionItemsNavBodyProps = {
	itemsCount: number;
	selectedFilters: SessionItemsNavFilterValue[];
	filters?: SessionItemsNavFilterValue[];
	onToggleFilter: (value: SessionItemsNavFilterValue) => void;
	children: React.ReactNode;
};

const SessionItemsNavBody = ({
	itemsCount,
	selectedFilters,
	filters,
	onToggleFilter,
	children,
}: SessionItemsNavBodyProps) => {
	const theme = useAppTheme();

	return (
		<Box
			flex={1}
			minHeight={0}
			flexDirection="column"
			backgroundColor={theme.colors.surface}
		>
			<Box style={{ flexShrink: 0 }}>
				<SessionItemsNavFiltersSection
					selectedFilters={selectedFilters}
					filters={filters}
					onToggleFilter={onToggleFilter}
				/>
				<SessionItemsNavItemsTitle itemCount={itemsCount} />
			</Box>
			<Box style={styles.listScrollHost}>{children}</Box>
		</Box>
	);
};

const resolvePresentation = (
	presentation: SessionItemsNavPaneProps["presentation"],
	device: ReturnType<typeof useDevice>,
) => {
	if (presentation && presentation !== "auto") {
		return presentation;
	}
	if (device.isLargeWindowSize) {
		return "inline" as const;
	}
	if (device.isMediumWindowSize) {
		return "side-sheet" as const;
	}
	return "bottom-sheet" as const;
};

export const SessionItemsNavPane = ({
	visible,
	onClose,
	title,
	items,
	onItemPress,
	activeItemIndex,
	filters,
	selectedFilters: selectedFiltersProp,
	onSelectedFiltersChange,
	defaultSelectedFilters = [],
	emptyStateTitle = SESSION_ITEMS_NAV_EMPTY_FILTERED_TITLE,
	emptyStateDescription = SESSION_ITEMS_NAV_EMPTY_FILTERED_SUBTITLE,
	emptyStateCta = SESSION_ITEMS_NAV_EMPTY_CTA,
	presentation = "auto",
}: SessionItemsNavPaneProps) => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const device = useDevice();
	const resolved = resolvePresentation(presentation, device);
	const bottomSheetRef = useRef<BottomSheetModal>(null);
	const isControlled = typeof onSelectedFiltersChange === "function";
	const [uncontrolledFilters, setUncontrolledFilters] = useState<
		SessionItemsNavFilterValue[]
	>(defaultSelectedFilters);
	const selectedFilters = isControlled
		? (selectedFiltersProp ?? [])
		: uncontrolledFilters;
	const filteredItems = useMemo(
		() =>
			items
				.map((item, originalIndex) => ({ item, originalIndex }))
				.filter(
					({ item }) =>
						!selectedFilters.length ||
						selectedFilters.some((filter) =>
							matchesSessionItemsNavFilter(item, filter),
						),
				),
		[selectedFilters, items],
	);
	const handleToggleFilter = (value: SessionItemsNavFilterValue) => {
		const next = selectedFilters.includes(value)
			? selectedFilters.filter((item) => item !== value)
			: [...selectedFilters, value];
		if (isControlled) {
			onSelectedFiltersChange?.(next);
		} else {
			setUncontrolledFilters(next);
		}
	};

	const clearSelectedFilters = useCallback(() => {
		if (isControlled) {
			onSelectedFiltersChange?.([]);
		} else {
			setUncontrolledFilters([]);
		}
	}, [isControlled, onSelectedFiltersChange]);

	useEffect(() => {
		if (visible && resolved === "bottom-sheet") {
			bottomSheetRef.current?.present();
		}
	}, [visible, resolved]);

	const renderBackdrop = useCallback(
		(props: BottomSheetBackdropProps) => (
			<BottomSheetBackdrop
				{...props}
				appearsOnIndex={0}
				disappearsOnIndex={-1}
				opacity={0.32}
				pressBehavior="close"
			/>
		),
		[],
	);

	const renderRow = useCallback(
		({
			item: entry,
		}: { item: { item: (typeof items)[number]; originalIndex: number } }) => (
			<SessionItemsNavRow
				item={entry.item}
				index={entry.originalIndex}
				isActive={activeItemIndex === entry.originalIndex}
				onPress={onItemPress}
			/>
		),
		[activeItemIndex, onItemPress],
	);

	const emptyList = useMemo(
		() => (
			<EmptyState
				title={
					items.length === 0
						? SESSION_ITEMS_NAV_EMPTY_NO_ITEMS_TITLE
						: emptyStateTitle
				}
				subtitle={
					items.length === 0
						? SESSION_ITEMS_NAV_EMPTY_NO_ITEMS_SUBTITLE
						: emptyStateDescription
				}
				image={SessionItemsNavEmptyIllustration}
				cta={items.length > 0 ? emptyStateCta : undefined}
				icon="filter-remove-outline"
				handleAction={items.length > 0 ? clearSelectedFilters : undefined}
			/>
		),
		[
			items.length,
			emptyStateTitle,
			emptyStateDescription,
			emptyStateCta,
			clearSelectedFilters,
		],
	);

	const resolvedTitle = title ?? t("session.navigation.questionsButtonLabel");

	if (resolved === "inline") {
		const navWidth = Math.min(screenWidth, 400);
		return (
			<Box
				style={[
					{
						width: navWidth,
						borderRightWidth: 1,
						borderRightColor: theme.colors.outlineVariant,
					},
					isWeb && { height: "100%" },
				]}
			>
				<Box
					flex={1}
					minHeight={0}
					flexDirection="column"
					style={isWeb ? { height: "100%" } : undefined}
				>
					<SessionItemsNavHeader title={resolvedTitle} onClose={onClose} />
					<SessionItemsNavBody
						itemsCount={filteredItems.length}
						selectedFilters={selectedFilters}
						filters={filters}
						onToggleFilter={handleToggleFilter}
					>
						<FlatList
							data={filteredItems}
							style={styles.list}
							contentContainerStyle={[
								styles.contentContainer,
								filteredItems.length === 0 && styles.emptyContentContainer,
							]}
							keyExtractor={({ item }) => item.id}
							ListEmptyComponent={emptyList}
							renderItem={renderRow}
						/>
					</SessionItemsNavBody>
				</Box>
			</Box>
		);
	}

	if (resolved === "side-sheet") {
		return (
			<SideSheet
				isVisible={visible}
				onClose={onClose}
				layout="modal"
				side="left"
				width={Math.min(screenWidth, 400)}
				title={resolvedTitle}
				hasArrow={false}
				hasActions={false}
				hasBackdrop
				hasCloseIcon
				borderRadiusStyle="modal"
				surfaceColor={theme.colors.surface}
			>
				<SessionItemsNavBody
					itemsCount={filteredItems.length}
					selectedFilters={selectedFilters}
					filters={filters}
					onToggleFilter={handleToggleFilter}
				>
					<FlatList
						data={filteredItems}
						style={styles.list}
						contentContainerStyle={[
							styles.contentContainer,
							filteredItems.length === 0 && styles.emptyContentContainer,
						]}
						keyExtractor={({ item }) => item.id}
						ListEmptyComponent={emptyList}
						renderItem={renderRow}
					/>
				</SessionItemsNavBody>
			</SideSheet>
		);
	}

	if (!visible) {
		return null;
	}

	return (
		<BottomSheetModal
			ref={bottomSheetRef}
			index={0}
			snapPoints={["94%"]}
			enablePanDownToClose
			enableDismissOnClose
			onDismiss={onClose}
			backdropComponent={renderBackdrop}
			handleComponent={() => (
				<Pressable
					onPress={onClose}
					accessibilityRole="button"
					accessibilityLabel={`Close ${resolvedTitle}`}
					style={styles.handleContainer}
				>
					<Box
						width={36}
						height={4}
						borderRadius={999}
						backgroundColor={theme.colors.outline}
					/>
				</Pressable>
			)}
			style={[
				styles.sheetShadow,
				{
					shadowColor: theme.colors.shadow,
					borderTopLeftRadius: theme.roundness * themeRoundnessMultiplier,
					borderTopRightRadius: theme.roundness * themeRoundnessMultiplier,
				},
			]}
			backgroundStyle={[
				styles.sheetBackground,
				{
					backgroundColor: theme.colors.surface,
					borderTopLeftRadius: theme.roundness * themeRoundnessMultiplier,
					borderTopRightRadius: theme.roundness * themeRoundnessMultiplier,
				},
			]}
		>
			<Box flex={1} minHeight={0} flexDirection="column">
				<SessionItemsNavHeader
					title={resolvedTitle}
					showClose={false}
					compact
				/>
				<SessionItemsNavBody
					itemsCount={filteredItems.length}
					selectedFilters={selectedFilters}
					filters={filters}
					onToggleFilter={handleToggleFilter}
				>
					<BottomSheetFlatList
						style={styles.list}
						data={filteredItems}
						keyExtractor={({ item }) => item.id}
						contentContainerStyle={
							filteredItems.length === 0
								? styles.emptyContentContainer
								: undefined
						}
						ListEmptyComponent={emptyList}
						renderItem={renderRow}
					/>
				</SessionItemsNavBody>
			</Box>
		</BottomSheetModal>
	);
};

const styles = StyleSheet.create({
	/** Hosts FlatList only; filters/title stay outside this scroll region. */
	listScrollHost: {
		flex: 1,
		minHeight: 0,
		flexBasis: 0,
		flexGrow: 1,
		flexShrink: 1,
		overflow: "hidden",
	},
	list: {
		flex: 1,
		minHeight: 0,
	},
	handleContainer: {
		paddingTop: 12,
		paddingBottom: 8,
		alignItems: "center",
	},
	sheetShadow: {
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 1,
		shadowRadius: 8,
		elevation: 8,
	},
	sheetBackground: {
		borderWidth: 0,
	},
	contentContainer: {
		paddingBottom: 0,
	},
	emptyContentContainer: {
		flexGrow: 1,
	},
});
